import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { validateDiscount } from "@/lib/api";

const CartContext = createContext(null);

const STORAGE_KEY = "st_cart_v1";
const DISCOUNT_KEY = "st_discount_v1";

const initial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.items)) {
        // Migrate legacy carts (no `key` field) to the new composite-key
        // structure so existing sessions keep working after the size update.
        const migrated = parsed.items.map((i) => ({
          ...i,
          size: i.size || null,
          key: i.key || (i.size ? `${i.id}::${i.size}` : i.id),
        }));
        return { items: migrated };
      }
    }
  } catch (_) {
    // ignore
  }
  return { items: [] };
};

// Composite line key so the same product in different sizes counts as
// separate cart rows. Bottles/notebooks have no size → key === productId.
const lineKey = (productId, size) => (size ? `${productId}::${size}` : productId);

function reducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const { product, size } = action;
      const key = lineKey(product.id, size);
      const exists = state.items.find((i) => i.key === key);
      if (exists) {
        return {
          items: state.items.map((i) =>
            i.key === key ? { ...i, quantity: Math.min(i.quantity + 1, 20) } : i
          ),
        };
      }
      return {
        items: [
          ...state.items,
          {
            key,
            id: product.id,
            name: product.name,
            price: product.price,
            image: product.image,
            size: size || null,
            quantity: 1,
          },
        ],
      };
    }
    case "INC":
      return {
        items: state.items.map((i) =>
          i.key === action.key ? { ...i, quantity: Math.min(i.quantity + 1, 20) } : i
        ),
      };
    case "DEC":
      return {
        items: state.items
          .map((i) => (i.key === action.key ? { ...i, quantity: i.quantity - 1 } : i))
          .filter((i) => i.quantity > 0),
      };
    case "REMOVE":
      return { items: state.items.filter((i) => i.key !== action.key) };
    case "CLEAR":
      return { items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, initial);
  const [open, setOpen] = useState(false);

  // Discount code state — the code string is persisted so it survives
  // page reloads; the validated `discount` object is re-fetched from the
  // backend whenever items change so a tampered cart can never keep an
  // outdated (larger) discount amount.
  const [discountCode, setDiscountCode] = useState(() => {
    try { return localStorage.getItem(DISCOUNT_KEY) || ""; } catch { return ""; }
  });
  const [discount, setDiscount] = useState(null); // full backend response
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  const revalidateRef = useRef(0);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {
      // ignore
    }
  }, [state]);

  // Re-validate the applied code whenever cart items change so the
  // discount amount stays in sync with the current subtotal (or gets
  // dropped if the cart empties).
  const subtotal = state.items.reduce((a, b) => a + b.quantity * b.price, 0);
  const shippingFee = subtotal >= 100 || subtotal === 0 ? 0 : 6.5;

  useEffect(() => {
    if (!discountCode) {
      setDiscount(null);
      setDiscountError(null);
      return;
    }
    if (state.items.length === 0) {
      // Keep the code stored but don't apply a discount on an empty bag.
      setDiscount(null);
      return;
    }
    const gen = ++revalidateRef.current;
    setDiscountLoading(true);
    validateDiscount(discountCode, subtotal, shippingFee)
      .then((res) => {
        if (gen !== revalidateRef.current) return; // stale
        if (res.ok) {
          setDiscount(res);
          setDiscountError(null);
        } else {
          setDiscount(null);
          setDiscountError(res.error || "That code isn't valid.");
        }
      })
      .catch(() => {
        if (gen !== revalidateRef.current) return;
        setDiscount(null);
        setDiscountError("Couldn't validate the code. Try again.");
      })
      .finally(() => {
        if (gen === revalidateRef.current) setDiscountLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountCode, subtotal, shippingFee, state.items.length]);

  useEffect(() => {
    try {
      if (discountCode) localStorage.setItem(DISCOUNT_KEY, discountCode);
      else localStorage.removeItem(DISCOUNT_KEY);
    } catch (_) { /* ignore */ }
  }, [discountCode]);

  const applyDiscount = useCallback(async (rawCode) => {
    const code = (rawCode || "").trim().toUpperCase();
    if (!code) {
      setDiscountCode("");
      setDiscount(null);
      setDiscountError("Enter a code.");
      return { ok: false, error: "Enter a code." };
    }
    setDiscountLoading(true);
    setDiscountError(null);
    try {
      const res = await validateDiscount(code, subtotal, shippingFee);
      if (res.ok) {
        setDiscountCode(code);
        setDiscount(res);
        return res;
      }
      setDiscount(null);
      setDiscountError(res.error || "That code isn't valid.");
      return res;
    } catch (e) {
      setDiscountError("Couldn't validate the code. Try again.");
      return { ok: false, error: "network" };
    } finally {
      setDiscountLoading(false);
    }
  }, [subtotal, shippingFee]);

  const removeDiscount = useCallback(() => {
    setDiscountCode("");
    setDiscount(null);
    setDiscountError(null);
  }, []);

  const value = useMemo(
    () => ({
      items: state.items,
      count: state.items.reduce((a, b) => a + b.quantity, 0),
      total: subtotal,
      subtotal,
      shippingFee,
      discountCode,
      discount,
      discountLoading,
      discountError,
      applyDiscount,
      removeDiscount,
      open,
      setOpen,
      add: (product, size) => {
        dispatch({ type: "ADD", product, size: size || null });
        setOpen(true);
      },
      addBulk: (items) => {
        items.forEach((p) => {
          const qty = Math.max(1, p.quantity || 1);
          for (let i = 0; i < qty; i++) {
            dispatch({ type: "ADD", product: p, size: p.size || null });
          }
        });
        setOpen(true);
      },
      inc: (key) => dispatch({ type: "INC", key }),
      dec: (key) => dispatch({ type: "DEC", key }),
      remove: (key) => dispatch({ type: "REMOVE", key }),
      clear: () => {
        dispatch({ type: "CLEAR" });
        setDiscountCode("");
        setDiscount(null);
      },
    }),
    [state, open, subtotal, shippingFee, discountCode, discount, discountLoading, discountError, applyDiscount, removeDiscount]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
